
"use client";

import { useRef, useState, useEffect } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible] as const;
}

function Reveal({ children, delay = 0, from = "bottom" }: {
  children: React.ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, v] = useVisible();
  const hidden = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text, center = false }: { text: string; center?: boolean }) {
  return (
    <span className={`block text-[11px] font-bold tracking-[0.15em] uppercase text-blue-700 mb-3 ${center ? "text-center" : ""}`}>
      {text}
    </span>
  );
}
function Divider({ center = false }: { center?: boolean }) {
  return <div className={`w-10 h-[3px] bg-blue-700 mb-5 ${center ? "mx-auto" : ""}`} />;
}

/* ─── Data ────────────────────────────────────────────────── */
const MAJOR_SCHEMES = [
  {
    title: "Credit Guarantee Schemes",
    short: "CGTMSE, CGFMU, CGSSI",
    body: "Collateral-free credit with government guarantee for MSMEs and startups — enabling access to formal credit without pledging assets.",
    benefit: "Up to ₹2 Crore",
    tag: "MSME",
    color: "bg-blue-700",
    img: "/images/services/GovernmentSchemes/Credit_Guarantee.png",
  },
  {
    title: "PMEGP",
    short: "Prime Minister's Employment Generation Programme",
    body: "Provides up to 35% subsidy for setting up new manufacturing or service enterprises in rural and urban areas.",
    benefit: "35% Subsidy",
    tag: "Employment",
    color: "bg-emerald-600",
    img: "/images/services/GovernmentSchemes/PMEGP.png",
  },
  {
    title: "Mudra Loans (PMMY)",
    short: "Pradhan Mantri Mudra Yojana",
    body: "Loans from ₹50,000 to ₹20 lakh for micro enterprises under Shishu, Kishor, and Tarun categories — no collateral required.",
    benefit: "Up to ₹20 Lakh",
    tag: "Micro Business",
    color: "bg-orange-500",
    img: "/images/services/GovernmentSchemes/Mudra_loan.png",
  },
  {
    title: "Stand-Up India",
    short: "SC/ST & Women Entrepreneurs",
    body: "Composite loans from ₹10 lakh to ₹1 crore for SC/ST and women entrepreneurs setting up greenfield enterprises.",
    benefit: "₹10L – ₹1 Crore",
    tag: "Inclusion",
    color: "bg-purple-600",
    img: "/images/services/GovernmentSchemes/Stand_india.png",
  },
  {
    title: "Startup India Seed Fund",
    short: "DPIIT Recognised Startups",
    body: "Grants up to ₹20 lakh for proof-of-concept and validation; investment up to ₹150 lakh for product development and market entry.",
    benefit: "Up to ₹1.5 Crore",
    tag: "Startup",
    color: "bg-blue-600",
    img: "/images/services/GovernmentSchemes/Startup_Seed_fund.png",
  },
  {
    title: "Production Linked Incentive (PLI)",
    short: "Sector-Specific Manufacturers",
    body: "5–20% production-linked financial incentive for companies in 14 key sectors including electronics, pharma, textiles, and food processing.",
    benefit: "5–20% Incentive",
    tag: "Manufacturing",
    color: "bg-teal-600",
    img: "/images/services/GovernmentSchemes/PLI.png",
  },
  {
    title: "CLCSS (Technology Upgradation)",
    short: "MSMEs in Manufacturing",
    body: "15% capital subsidy for technology upgradation of plant and machinery. Available to MSMEs across eligible sectors.",
    benefit: "15% Capital Subsidy",
    tag: "Technology",
    color: "bg-indigo-600",
    img: "/images/services/GovernmentSchemes/CLCSS.png",
  },
  {
    title: "PM Vishwakarma Yojana",
    short: "Traditional Artisans & Craftspeople",
    body: "Skill training, toolkit support, and collateral-free credit up to ₹3 lakh for artisans and craftspeople in 18 trades.",
    benefit: "Up to ₹3 Lakh",
    tag: "Artisans",
    color: "bg-amber-600",
    img: "/images/services/GovernmentSchemes/PM_Vishwakarma.png",
  },
  {
    title: "NIDHI Programme (DST)",
    short: "Deep-Tech & Innovative Startups",
    body: "National Initiative for Developing and Harnessing Innovations — funding up to ₹50 lakh for incubation and deep-tech startups.",
    benefit: "Up to ₹50 Lakh",
    tag: "Deep-Tech",
    color: "bg-cyan-600",
    img: "/images/services/GovernmentSchemes/NIDHI.png",
  },
];

const SPECIALIZED_SERVICES = [
  {
    title: "NAIFF Funding",
    body: "Government-backed funding support to scale MSMEs and startups through the National Agri Infra Financing Facility.",
    tag: "Agri & MSME",
    color: "bg-rose-500",
    img: "/images/services/GovernmentSchemes/NAIFF.png",
  },
  {
    title: "Tax Exemption Benefits",
    body: "Income tax, GST, and compliance exemptions for eligible businesses — 80-IAC, 12AB, 80G, and sector-specific schemes.",
    tag: "Tax Relief",
    color: "bg-blue-600",
    img: "/images/services/GovernmentSchemes/Tax_Exemption.png",
  },
  {
    title: "Seed Funding Access",
    body: "Early-stage capital support for innovative startups through government-backed incubation and angel networks.",
    tag: "Early Stage",
    color: "bg-amber-500",
    img: "/images/services/GovernmentSchemes/Seed_Funding.png",
  },
  {
    title: "Venture Capital Support",
    body: "Equity and VC access for high-growth potential ventures through SIDBI Fund of Funds and government-linked AIFs.",
    tag: "Growth Stage",
    color: "bg-purple-600",
    img: "/images/services/GovernmentSchemes/Venture _Support.png",
  },
  {
    title: "GeM Registration & Selling",
    body: "Sell directly to government buyers on the Government eMarketplace — access thousands of government procurement orders.",
    tag: "Government Sales",
    color: "bg-emerald-600",
    img: "/images/services/GovernmentSchemes/GeM_Registration.png",
  },
  {
    title: "Export Promotion Support",
    body: "MEIS, RoDTEP, and EPCG schemes — subsidies and incentives for businesses involved in international trade and exports.",
    tag: "Export",
    color: "bg-teal-600",
    img: "/images/services/GovernmentSchemes/Export_Promotion.png",
  },
  {
    title: "R&D & Innovation Grants",
    body: "DST, DBT, and BIRAC grants for research, development, and technology innovation in priority sectors.",
    tag: "Innovation",
    color: "bg-indigo-600",
    img: "/images/services/GovernmentSchemes/R&D.png",
  },
  {
    title: "Infrastructure Capital Subsidy",
    body: "State and central capital subsidies for land, power, water, and infrastructure for businesses in industrial zones.",
    tag: "Infrastructure",
    color: "bg-orange-500",
    img: "/images/services/GovernmentSchemes/Infrastructure.png",
  },
];

const STATE_SCHEMES = [
  { state: "Maharashtra",    scheme: "MIDC Industrial Incentives",       benefit: "Land + power subsidy" },
  { state: "Gujarat",        scheme: "GIDC & Vibrant Gujarat",           benefit: "Capital subsidy up to 10%" },
  { state: "Rajasthan",      scheme: "RIICO Industrial Area",            benefit: "Land allotment + interest subsidy" },
  { state: "Tamil Nadu",     scheme: "SIPCOT Incentives",                benefit: "Tax holiday + subsidy" },
  { state: "Uttar Pradesh",  scheme: "MSME Promotion Policy",            benefit: "25% capital subsidy" },
  { state: "Karnataka",      scheme: "Elevate Karnataka",                benefit: "Startup grants up to ₹50L" },
  { state: "Telangana",      scheme: "T-Hub & T-Works",                  benefit: "Incubation + funding" },
  { state: "Haryana",        scheme: "Enterprise Promotion Policy",      benefit: "Employment + power subsidy" },
];

const CERTIFICATIONS = [
  { title: "Udyam / MSME Registration",  body: "Unlock loans, subsidies, and government tenders instantly with this mandatory MSME recognition.", img: "/images/services/GovernmentSchemes/Udhyam.png" },
  { title: "DPIIT / Startup India",      body: "3-year tax holiday, angel tax exemption, and seed fund access for recognised startups.", img: "/images/services/GovernmentSchemes/DPIIT.png" },
  { title: "ISO Certifications",         body: "ISO 9001, 14001, 27001 — improve credibility, win large contracts, and meet export requirements.", img: "/images/services/GovernmentSchemes/ISO.png" },
  { title: "ZED Certification",          body: "Zero Defect Zero Effect — manufacturing quality certification that improves global competitiveness.", img: "/images/services/GovernmentSchemes/Zed_certifications.png" },
  { title: "FSSAI Registration",         body: "Mandatory food safety certification for all food manufacturers, traders, and restaurants in India.", img: "/images/services/GovernmentSchemes/FSSAI.png" },
  { title: "NSIC Registration",          body: "Tender benefits, raw material support, and marketing assistance for registered small industries.", img: "/images/services/GovernmentSchemes/NSIC.png" },
];

const STATS = [
  { v: "₹10,000Cr+", l: "Total funding disbursed annually" },
  { v: "50L+",        l: "Entrepreneurs supported" },
  { v: "500+",        l: "Schemes & programs available" },
  { v: "95%+",        l: "Application success rate with Farsight" },
];

/* ─── Scheme Card ─────────────────────────────────────────── */
function SchemeCard({ s, i, cols = 3 }: { s: typeof MAJOR_SCHEMES[0]; i: number; cols?: number }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`bg-white border border-slate-200 p-2 flex flex-col transition-all duration-500 hover:shadow-lg hover:border-slate-300 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${(i % cols) * 0.08}s` }}
    >
      {/* Top accent bar */}
      <div className={`w-8 h-1 ${s.color} mb-4`} />

      {/* Added Dummy Image */}
      <img
        src={s.img}
        alt={s.title}
        className="w-full h-[250px] object-cover mb-4 rounded-sm border border-slate-100"
      />

      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[14px] font-bold text-slate-900 leading-snug">{s.title}</p>
          <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{s.short}</p>
        </div>
        <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide whitespace-nowrap flex-shrink-0">{s.tag}</span>
      </div>

      <p className="text-[13px] text-slate-500 leading-relaxed flex-1 mb-4">{s.body}</p>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <div>
          <p className="text-[10px] font-bold text-slate-400 tracking-[0.08em] uppercase mb-0.5">Key Benefit</p>
          <p className="text-[13px] font-black text-blue-700">{s.benefit}</p>
        </div>
        <Link href="/services/funding" className="text-[12px] font-bold text-blue-700 tracking-[0.04em] uppercase flex items-center gap-1 hover:gap-2 transition-all">
          Learn More
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6h8M7 3l3 3-3 3" stroke="#1565C0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>
      </div>
    </div>
  );
}

/* ─── Specialized Service Card ────────────────────────────── */
function SpecCard({ s, i }: { s: typeof SPECIALIZED_SERVICES[0]; i: number }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`bg-white border border-slate-200 p-2 flex flex-col transition-all duration-500 hover:shadow-md hover:border-slate-300 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${(i % 4) * 0.08}s` }}
    >
      <div className={`w-7 h-1 ${s.color} mb-3`} />

      {/* Added Dummy Image */}
      <img
        src={s.img}
        alt={s.title}
        className="w-full h-[250px] object-cover mb-4 rounded-sm border border-slate-100"
      />

      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[13.5px] font-bold text-slate-900 leading-snug">{s.title}</p>
        <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide whitespace-nowrap flex-shrink-0">{s.tag}</span>
      </div>
      <p className="text-[13px] text-slate-500 leading-relaxed flex-1 mb-4">{s.body}</p>
      <Link href="/services/funding" className="text-[12px] font-bold text-blue-700 tracking-[0.04em] uppercase flex items-center gap-1 hover:gap-2 transition-all">
        Get Started
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 6h8M7 3l3 3-3 3" stroke="#1565C0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Link>
    </div>
  );
}

/* ─── Cert Card ───────────────────────────────────────────── */
function CertCard({ c, i }: { c: typeof CERTIFICATIONS[0]; i: number }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`bg-white border border-slate-200 p-2 flex flex-col transition-all duration-500 hover:shadow-md hover:border-blue-200 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${(i % 3) * 0.08}s` }}
    >
      {/* Added Dummy Image */}
      <img
        src={c.img}
        alt={c.title}
        className="w-full h-[250px] object-cover mb-4 rounded-sm border border-slate-100"
      />

      <div className="flex items-center gap-3 mb-3">
        <div className="w-7 h-7 bg-blue-700 flex items-center justify-center flex-shrink-0">
          <span className="text-[10px] font-black text-white">{String(i + 1).padStart(2, "0")}</span>
        </div>
        <p className="text-[13.5px] font-bold text-slate-900 leading-snug">{c.title}</p>
      </div>
      <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{c.body}</p>
    </div>
  );
}

/* ─── ProcessStepCard ─────────────────────────────────────── */
function ProcessStepCard({ step, i }: { step: { n: string; title: string; body: string }; i: number }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`bg-white border border-slate-200 p-7 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${i * 0.1}s` }}
    >
      <div className="w-9 h-9 bg-blue-700 flex items-center justify-center mb-5">
        <span className="text-[12px] font-black text-white">{step.n}</span>
      </div>
      <p className="text-[14px] font-bold text-slate-900 mb-2.5">{step.title}</p>
      <p className="text-[13px] text-slate-500 leading-relaxed">{step.body}</p>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function GovernmentSchemesPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="pt-10 sm:pt-14 lg:pt-20 pb-8 sm:pb-12 lg:pb-16 px-4 sm:px-8 lg:px-20 bg-slate-50 text-center">
        <Reveal>
          <Eyebrow text="Government Support Programs" center />
          <h1 className="text-[clamp(30px,4vw,54px)] font-extrabold text-slate-900 leading-tight mb-5 max-w-[700px] mx-auto">
            All Government{" "}
            <span className="text-blue-700">Schemes &amp; Programs</span>
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed max-w-[580px] mx-auto mb-10">
            Comprehensive guide to 500+ government funding schemes, subsidies, tax benefits, and support programs for startups, MSMEs, and entrepreneurs across India.
          </p>

          {/* Quick stat strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 max-w-[860px] mx-auto gap-0.5 mb-10">
            {STATS.map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 px-6 py-5">
                <p className="text-lg sm:text-xl lg:text-[26px] font-black text-blue-700 leading-none mb-1">{s.v}</p>
                <p className="text-[12px] text-slate-400 leading-snug">{s.l}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-3 justify-center flex-wrap">
            <Link
              href="/services/funding"
              className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors"
            >
              GET EXPERT CONSULTATION
            </Link>
            <Link
              href="#eligibility"
              className="bg-white text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors"
            >
              CHECK YOUR ELIGIBILITY
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ══ MAJOR SCHEMES — 3-col grid (9 cards) ══════════ */}
      <section className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="text-center mb-8 lg:mb-12">
              <Eyebrow text="Direct Government Schemes" center />
              <Divider center />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-3">
                Major Government Schemes
              </h2>
              <p className="text-[15px] text-slate-500 max-w-[560px] mx-auto leading-relaxed">
                Direct government-backed programs with dedicated portals and implementation frameworks — available for eligible Indian businesses today.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MAJOR_SCHEMES.map((s, i) => <SchemeCard key={i} s={s} i={i} cols={3} />)}
          </div>
        </div>
      </section>

      {/* ══ SPECIALIZED FUNDING — 4-col grid (8 cards) ════ */}
      <section className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="text-center mb-8 lg:mb-12">
              <Eyebrow text="Expert-Assisted Services" center />
              <Divider center />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-3">
                Specialized Funding Services
              </h2>
              <p className="text-[15px] text-slate-500 max-w-[560px] mx-auto leading-relaxed">
                Expert-assisted programs and funding mechanisms to help you access government benefits — from seed capital to venture funding and export incentives.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SPECIALIZED_SERVICES.map((s, i) => <SpecCard key={i} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ══ STATE SCHEMES — full table ═════════════════════ */}
      <section className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center mb-8 lg:mb-12">
              <div>
                <Eyebrow text="State-Level Programs" />
                <Divider />
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-3">
                  State Industrial Incentives
                </h2>
                <p className="text-[15px] text-slate-500 leading-relaxed">
                  Every Indian state offers its own package of capital subsidies, land incentives, and tax holidays. We help you identify and access the best state scheme for your business location and industry.
                </p>
              </div>
              <img
                src="/images/services/GovernmentSchemes/Infrastructure.png"
                alt="State government schemes"
                className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover bg-slate-100 border border-slate-200"
              />
            </div>
          </Reveal>

          <Reveal>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-blue-700">
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold text-white tracking-[0.1em] uppercase">State</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold text-white tracking-[0.1em] uppercase">Key Scheme</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold text-white tracking-[0.1em] uppercase">Key Benefit</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-bold text-white tracking-[0.1em] uppercase"></th>
                  </tr>
                </thead>
                <tbody>
                  {STATE_SCHEMES.map((row, i) => (
                    <tr key={i} className={`border-b border-slate-200 hover:bg-blue-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                      <td className="px-5 py-4 text-[13.5px] font-bold text-slate-900">{row.state}</td>
                      <td className="px-5 py-4 text-[13.5px] text-slate-600">{row.scheme}</td>
                      <td className="px-5 py-4">
                        <span className="text-[12px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1">{row.benefit}</span>
                      </td>
                      <td className="px-5 py-4">
                        <Link href="/services/funding" className="text-[11px] font-bold text-blue-700 tracking-[0.04em] uppercase flex items-center gap-1 hover:gap-2 transition-all">
                          Apply <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="#1565C0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CERTIFICATIONS — 3-col grid (6 cards) ══════════ */}
      <section className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="text-center mb-8 lg:mb-12">
              <Eyebrow text="Business Certifications" center />
              <Divider center />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-3">
                Certifications That Unlock Benefits
              </h2>
              <p className="text-[15px] text-slate-500 max-w-[560px] mx-auto leading-relaxed">
                The right certification can unlock loans, subsidies, government tenders, and tax exemptions. Farsight manages the entire process from eligibility to approval.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CERTIFICATIONS.map((c, i) => <CertCard key={i} c={c} i={i} />)}
          </div>
        </div>
      </section>

      {/* ══ HOW FARSIGHT HELPS — split ══════════════════════ */}
      <section className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <img
              src="/images/services/GovernmentSchemes/DPIIT.png"
              alt="Government scheme experts"
              className="w-full h-[240px] sm:h-[340px] lg:h-[420px] object-cover bg-slate-100 border border-slate-200"
            />
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <Eyebrow text="How Farsight Helps" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              End-to-End Government Scheme Support
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              Navigating 500+ government schemes is complex. Farsight&apos;s specialists identify the right programs for your business, assess eligibility, prepare applications, and manage post-approval compliance — so you capture every benefit you are entitled to.
            </p>

            <div className="flex flex-col gap-2">
              {[
                { n: "01", title: "Scheme Identification",      body: "We map your business profile against 500+ central, state, and sector-specific schemes." },
                { n: "02", title: "Eligibility Assessment",     body: "Free first-session assessment — we confirm eligibility before any time or cost commitment." },
                { n: "03", title: "Application & Documentation",body: "We prepare and submit all documents and manage the end-to-end application process." },
                { n: "04", title: "Approval & Compliance",      body: "We track applications, handle queries, and ensure post-award compliance." },
              ].map((step, i) => (
                <div key={i} className="flex gap-4 items-start bg-slate-50 border border-slate-200 px-5 py-4">
                  <div className="w-7 h-7 bg-blue-700 flex-shrink-0 flex items-center justify-center">
                    <span className="text-[10px] font-black text-white">{step.n}</span>
                  </div>
                  <div>
                    <p className="text-[13.5px] font-bold text-slate-900 mb-0.5">{step.title}</p>
                    <p className="text-[13px] text-slate-500 leading-relaxed">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ PROCESS — 4 steps horizontal ═══════════════════ */}
      <section className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="text-center mb-8 lg:mb-12">
              <Eyebrow text="Our Process" center />
              <Divider center />
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight">
                From Consultation to Approval — In 4 Steps
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              { n: "01", title: "Free Consultation",        body: "We review your business profile and identify all applicable schemes and certifications — free, no commitment." },
              { n: "02", title: "Eligibility Mapping",      body: "We assess eligibility across all relevant programs and advise on the optimal application sequence." },
              { n: "03", title: "Application Execution",    body: "We prepare and submit every document, form, and portal registration with 100% accuracy." },
              { n: "04", title: "Approval & Post-Support",  body: "We track progress, handle queries from authorities, and manage post-approval compliance requirements." },
            ].map((step, i) => (
              <ProcessStepCard key={i} step={step} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ ELIGIBILITY CHECKER CTA ════════════════════════ */}
      <section className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20 bg-white" id="eligibility">
        <div className="max-w-[1160px] mx-auto">
          <div className="bg-slate-50 border border-slate-200 p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <Reveal from="left">
              <Eyebrow text="Need Help Choosing the Right Scheme?" />
              <Divider />
              <h2 className="text-[clamp(22px,2.8vw,36px)] font-bold text-slate-900 leading-tight mb-5">
                Let Our Experts Find the Best Program for Your Business
              </h2>
              <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
                Our experts can help you identify the most suitable government schemes for your business profile and assist with the complete application process — free initial assessment, no obligation.
              </p>
              <div className="flex gap-3.5 flex-wrap">
                <Link
                  href="/services/funding"
                  className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors"
                >
                  GET EXPERT CONSULTATION
                </Link>
                <Link
                  href="/contact"
                  className="bg-white text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors"
                >
                  CHECK YOUR ELIGIBILITY
                </Link>
              </div>
            </Reveal>

            <Reveal from="right" delay={0.12}>
              {/* Quick eligibility highlights */}
              <div className="flex flex-col gap-3">
                <p className="text-[12px] font-bold text-slate-400 tracking-[0.1em] uppercase mb-1">You may be eligible if:</p>
                {[
                  "Your business is registered in India (any structure)",
                  "You are an MSME, startup, or entrepreneur",
                  "Your annual turnover is below ₹250 crore",
                  "You are in manufacturing, services, or agri-processing",
                  "You are a woman, SC/ST, or first-generation entrepreneur",
                  "Your startup is less than 10 years old",
                ].map((pt, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white border border-slate-100 px-4 py-3">
                    <div className="w-4 h-4 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-[13px] text-slate-600 leading-snug">{pt}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

    </main>
  );
}
